import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DollarSign, Cpu, Image, Headphones, FileText, Calculator, TrendingUp, Layers, Info, Plus, Trash2, Tag, ToggleLeft, ToggleRight, Percent, Send, CheckCircle, Ticket, Copy, Eye } from 'lucide-react';
import KineticHero from '@/components/KineticHero';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COST_DATA = {
  text: { model: 'Intelligence Engine', provider: 'Anthropic (via Emergent)', inputRate: 5.00, outputRate: 25.00, unit: '1M tokens' },
  image: { model: 'Gemini Nano Banana', provider: 'Google (via Emergent)', rate: 0.04, unit: 'per image' },
  // Claude HTML→PNG fallback: ~600 input + ~6000 output tokens per illustration
  // ⇒ (600 × 5 + 6000 × 25) / 1e6 = $0.153 per image. Kicks in only when admin
  // toggles the Illustration Engine to Claude via /platform/admin → User Management.
  image_claude: { model: 'Claude HTML → PNG', provider: 'Anthropic + Playwright Chromium (via Emergent)', rate: 0.15, unit: 'per image (fallback)', inputTokens: 600, outputTokens: 6000 },
  audio: { model: 'OpenAI TTS', provider: 'OpenAI (via Emergent)', rate: 15.00, unit: '1M characters' },
};

const REPORT_COMPONENTS = [
  { id: 'scout_sections', label: '12 Scout Sections', icon: FileText, calls: 12, inputTokens: 5000, outputTokens: 2000, type: 'text' },
  { id: 'strategist_sections', label: '10 Strategist Sections (chunked)', icon: FileText, calls: 20, inputTokens: 4000, outputTokens: 1800, type: 'text' },
  { id: 'sovereign_sections', label: '8 Sovereign Sections (chunked)', icon: FileText, calls: 16, inputTokens: 4000, outputTokens: 1800, type: 'text' },
  { id: 'exec_brief', label: 'AI Executive Brief (PDF)', icon: FileText, calls: 1, inputTokens: 8000, outputTokens: 1500, type: 'text' },
  { id: 'references', label: 'Sources & References', icon: FileText, calls: 1, inputTokens: 4000, outputTokens: 1500, type: 'text' },
  { id: 'illustrations', label: '31 AI Illustrations', icon: Image, calls: 31, type: 'image' },
  { id: 'infographic', label: 'Investment Infographic', icon: Image, calls: 1, type: 'image' },
  { id: 'audio', label: 'Audio Overview (TTS)', icon: Headphones, calls: 1, characters: 5000, type: 'audio' },
  { id: 'pdf', label: 'PDF Export (ReportLab)', icon: FileText, calls: 1, type: 'free' },
];

function calcCost(c) {
  if (c.type === 'text') return (c.calls * c.inputTokens * COST_DATA.text.inputRate + c.calls * c.outputTokens * COST_DATA.text.outputRate) / 1e6;
  if (c.type === 'image') return c.calls * COST_DATA.image.rate;
  if (c.type === 'audio') return (c.characters * COST_DATA.audio.rate) / 1e6;
  return 0;
}

const TIERS = [
  { name: 'SCOUT', color: '#004ac6', tagline: 'Reconnaissance Intelligence — 12 sections', includes: ['scout_sections', 'exec_brief', 'references', 'pdf'] },
  { name: 'STRATEGIST', color: '#00A651', tagline: 'Strategic Execution — 22 sections', includes: ['scout_sections', 'strategist_sections', 'exec_brief', 'references', 'illustrations', 'infographic', 'pdf'] },
  { name: 'SOVEREIGN', color: '#7c3aed', tagline: 'Deal-Ready Intelligence — 31 sections + Audio Brief', includes: ['scout_sections', 'strategist_sections', 'sovereign_sections', 'exec_brief', 'references', 'illustrations', 'infographic', 'audio', 'pdf'] },
];

export default function PricingPage() {
  const [markup, setMarkup] = useState(1000);
  const [savedMarkup, setSavedMarkup] = useState(1000);
  const [promotions, setPromotions] = useState([]);
  const [savedPromos, setSavedPromos] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [newPromo, setNewPromo] = useState({ name: '', discount: 10, active: true });
  const [coupons, setCoupons] = useState([]);
  const [newCoupon, setNewCoupon] = useState({ code: '', reports: 1, max_uses: 10 });
  const [showDiscount, setShowDiscount] = useState(true);
  const [showCoupon, setShowCoupon] = useState(true);
  const [showLucky, setShowLucky] = useState(true);
  const [savedToggles, setSavedToggles] = useState({ d: true, c: true, l: true });

  const hasChanges = markup !== savedMarkup || JSON.stringify(promotions) !== JSON.stringify(savedPromos) || showDiscount !== savedToggles.d || showCoupon !== savedToggles.c || showLucky !== savedToggles.l;

  useEffect(() => {
    axios.get(`${API}/pricing-config`).then(res => {
      const m = res.data.markup || 1000;
      const p = res.data.promotions || [];
      setMarkup(m); setSavedMarkup(m);
      setPromotions(p); setSavedPromos(p);
      const sd = res.data.show_discount_bar !== false;
      const sc = res.data.show_coupon_bar !== false;
      const sl = res.data.show_lucky_bar !== false;
      setShowDiscount(sd); setShowCoupon(sc); setShowLucky(sl);
      setSavedToggles({ d: sd, c: sc, l: sl });
    }).catch(() => {});
    fetchCoupons();
  }, []);

  const fetchCoupons = () => {
    axios.get(`${API}/admin/coupons`).then(res => setCoupons(res.data)).catch(() => {});
  };

  const addCoupon = async () => {
    if (!newCoupon.code.trim()) return toast.error('Enter a coupon code');
    try {
      await axios.post(`${API}/admin/coupons`, newCoupon);
      toast.success(`Coupon ${newCoupon.code.toUpperCase()} created`);
      setNewCoupon({ code: '', reports: 1, max_uses: 10 });
      fetchCoupons();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteCoupon = async (id) => {
    try {
      await axios.delete(`${API}/admin/coupons/${id}`);
      toast.success('Coupon deleted');
      fetchCoupons();
    } catch (err) { toast.error('Failed'); }
  };

  const publishToCustomers = async () => {
    setPublishing(true);
    try {
      // Compute base_costs from REPORT_COMPONENTS so the public page renders
      // identical sell prices (Scout/Strategist/Sovereign keys match public page).
      const baseCosts = TIERS.reduce((acc, t) => {
        const cost = REPORT_COMPONENTS
          .filter(c => t.includes.includes(c.id))
          .reduce((s, c) => s + calcCost(c), 0);
        // t.name is 'SCOUT' | 'STRATEGIST' | 'SOVEREIGN' — normalize to Title case
        acc[t.name.charAt(0) + t.name.slice(1).toLowerCase()] = Math.round(cost * 100) / 100;
        return acc;
      }, {});
      await axios.put(`${API}/admin/pricing-config`, {
        markup, promotions,
        base_costs: baseCosts,
        show_discount_bar: showDiscount,
        show_coupon_bar: showCoupon,
        show_lucky_bar: showLucky,
      });
      setSavedMarkup(markup);
      setSavedPromos(JSON.parse(JSON.stringify(promotions)));
      setSavedToggles({ d: showDiscount, c: showCoupon, l: showLucky });
      toast.success('Pricing published to customer page!');
    } catch { toast.error('Publish failed'); }
    finally { setPublishing(false); }
  };

  const addPromo = () => {
    if (!newPromo.name.trim()) return toast.error('Enter promotion name');
    if (newPromo.discount < 1 || newPromo.discount > 99) return toast.error('Discount must be 1-99%');
    setPromotions(prev => [...prev, { ...newPromo, id: Date.now().toString(), created_at: new Date().toISOString() }]);
    setNewPromo({ name: '', discount: 10, active: true });
  };

  const removePromo = (id) => setPromotions(prev => prev.filter(p => p.id !== id));
  const togglePromo = (id) => setPromotions(prev => prev.map(p => p.id === id ? { ...p, active: !p.active } : p));

  const activePromo = promotions.find(p => p.active);
  const tierCosts = TIERS.map(t => ({
    ...t,
    cost: REPORT_COMPONENTS.filter(c => t.includes.includes(c.id)).reduce((s, c) => s + calcCost(c), 0),
    components: REPORT_COMPONENTS.filter(c => t.includes.includes(c.id)),
  }));

  return (
    <div data-testid="pricing-page" style={{ fontFamily: "'Inter', sans-serif" }}>
      <KineticHero
        uid="prccfg"
        palette="amber"
        eyebrow="Admin · tiers, coupons, publish"
        title="Pricing"
        accent="Model"
        subtitle="Adjust pricing, promotions, and coupon visibility — then publish to the customer-facing page in one click."
        right={
          <div className="flex items-center gap-2">
            <a href="/pricing" target="_blank" rel="noopener noreferrer"
               data-testid="view-public-pricing-btn"
               className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold border border-[#c3c6d7]/40 bg-white text-[#434655] hover:bg-[#eff4ff] hover:text-[#004ac6] transition-all">
              <Eye className="w-4 h-4" /> View Public Pricing
            </a>
            <button onClick={publishToCustomers} disabled={publishing || !hasChanges}
              data-testid="publish-btn"
              className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-bold transition-all ${hasChanges ? 'bg-[#004ac6] text-white hover:bg-[#003ea8] shadow-lg shadow-[#004ac6]/20' : 'bg-[#c3c6d7]/30 text-[#737686] cursor-not-allowed'}`}>
              {publishing ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Publishing...</> :
                hasChanges ? <><Send className="w-4 h-4" /> Publish to Customers</> :
                <><CheckCircle className="w-4 h-4" /> Published</>}
            </button>
          </div>
        }
      />
      <div className="p-6 lg:p-8 space-y-8">

      {/* Visibility Toggles */}
      <div className="bg-white rounded-xl shadow-sm p-5 flex items-center gap-6">
        <span className="text-xs font-bold text-[#434655] uppercase tracking-widest">Customer Page Sections</span>
        {[
          { label: 'Discount Banner', val: showDiscount, set: setShowDiscount, clr: '#004ac6' },
          { label: 'Coupon Redeem', val: showCoupon, set: setShowCoupon, clr: '#7c3aed' },
          { label: 'Be Lucky Requests', val: showLucky, set: setShowLucky, clr: '#d97706' },
        ].map(t => (
          <button key={t.label} onClick={() => t.set(!t.val)} data-testid={`toggle-${t.label.toLowerCase().replace(/\s/g, '-')}`}
            className="flex items-center gap-2 text-xs cursor-pointer">
            {t.val ? <ToggleRight className="w-5 h-5" style={{ color: t.clr }} /> : <ToggleLeft className="w-5 h-5 text-[#c3c6d7]" />}
            <span className={t.val ? 'font-semibold text-[#0b1c30]' : 'text-[#c3c6d7]'}>{t.label}</span>
          </button>
        ))}
      </div>

      {/* Markup Slider + Preview */}
      <section className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-bold uppercase tracking-widest text-[#434655] flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-[#004ac6]" /> Price Markup
          </h2>
          <div className="flex items-center gap-3">
            {hasChanges && <span className="text-[10px] font-bold text-amber-500 bg-amber-50 px-2 py-0.5 rounded-full">Unsaved changes</span>}
            <span className="text-lg font-extrabold text-[#004ac6]">{markup.toLocaleString()}x</span>
          </div>
        </div>
        <input type="range" min="100" max="2000" step="50" value={markup}
          onChange={e => setMarkup(+e.target.value)}
          className="w-full accent-[#004ac6] h-2" data-testid="markup-slider" />
        <div className="flex justify-between text-[10px] text-[#737686] mt-1">
          <span>100x</span><span>500x</span><span>1,000x</span><span>1,500x</span><span>2,000x</span>
        </div>
      </section>

      {/* Detailed Tier Cards (from screenshot) */}
      <section>
        <h2 className="text-sm font-bold uppercase tracking-widest text-[#434655] mb-3 flex items-center gap-2">
          <Layers className="w-4 h-4 text-[#004ac6]" /> Pricing Tiers — Detailed Cost Analysis
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tierCosts.map(tier => {
            const sellPrice = tier.cost * markup;
            const discountedSell = activePromo ? sellPrice * (1 - activePromo.discount / 100) : null;
            const displaySell = discountedSell || sellPrice;
            const margin = ((displaySell - tier.cost) / displaySell * 100);
            return (
              <div key={tier.name} className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid={`tier-${tier.name.toLowerCase()}`}>
                {/* Colored Header */}
                <div className="px-6 py-5" style={{ backgroundColor: tier.color }}>
                  <p className="text-white text-xl font-extrabold tracking-tight">{tier.name}</p>
                  <p className="text-white/60 text-[10px] uppercase tracking-widest mt-0.5">{tier.tagline}</p>
                </div>
                <div className="p-6 space-y-4">
                  {/* Your Cost */}
                  <div>
                    <p className="text-[10px] text-[#434655] uppercase tracking-widest">Your Cost</p>
                    <p className="text-2xl font-extrabold text-[#0b1c30]">${tier.cost.toFixed(2)} <span className="text-xs font-medium text-[#737686]">per report</span></p>
                  </div>
                  <div className="h-px bg-[#c3c6d7]/30" />
                  {/* Sell Price */}
                  <div>
                    <p className="text-[10px] text-[#434655] uppercase tracking-widest">
                      Sell Price ({markup.toLocaleString()}x){activePromo ? ` — ${activePromo.discount}% off` : ''}
                    </p>
                    {discountedSell ? (
                      <div className="flex items-baseline gap-2">
                        <p className="text-2xl font-extrabold" style={{ color: tier.color }}>${discountedSell.toFixed(2)}</p>
                        <span className="text-sm text-[#c3c6d7] line-through">${sellPrice.toFixed(2)}</span>
                        <span className="text-xs font-medium text-[#737686]">per report</span>
                      </div>
                    ) : (
                      <p className="text-2xl font-extrabold" style={{ color: tier.color }}>${sellPrice.toFixed(2)} <span className="text-xs font-medium text-[#737686]">per report</span></p>
                    )}
                  </div>
                  {/* Gross Margin */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#434655]">Gross Margin</span>
                    <span className="text-sm font-bold text-green-600">{margin.toFixed(1)}%</span>
                  </div>
                  <div className="h-px bg-[#c3c6d7]/30" />
                  {/* Includes */}
                  <div>
                    <p className="text-[10px] text-[#434655] uppercase tracking-widest mb-2">Includes</p>
                    <ul className="space-y-1.5">
                      {tier.components.map(c => {
                        const Icon = c.icon;
                        const cost = calcCost(c);
                        return (
                          <li key={c.id} className="flex items-center gap-2 text-xs text-[#434655]">
                            <Icon className="w-3.5 h-3.5 text-[#004ac6]" />
                            <span className="flex-1">{c.label}</span>
                            <span className="text-[#737686]">{cost === 0 ? 'Free' : `$${cost.toFixed(2)}`}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Promotions & Discounts */}
      <section className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-sm font-bold uppercase tracking-widest text-[#434655] flex items-center gap-2 mb-5">
          <Tag className="w-4 h-4 text-[#004ac6]" /> Promotions & Discounts
        </h2>
        <p className="text-xs text-[#737686] mb-4">Active promotions appear as a banner on the public pricing page after you publish. Only the first active promotion applies.</p>

        {/* Add New */}
        <div className="bg-[#f8f9ff] rounded-xl p-4 mb-5">
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#434655] mb-3">Add New Promotion</p>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="text-[10px] text-[#434655] block mb-1">Promotion Name</label>
              <input type="text" value={newPromo.name} onChange={e => setNewPromo(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Launch Week Special, Early Bird Discount"
                className="w-full bg-white border border-[#c3c6d7]/40 rounded-lg px-3 py-2 text-sm text-[#0b1c30] focus:ring-1 focus:ring-[#004ac6] outline-none"
                data-testid="promo-name-input" />
            </div>
            <div className="w-28">
              <label className="text-[10px] text-[#434655] block mb-1">Discount %</label>
              <input type="number" min="1" max="99" value={newPromo.discount} onChange={e => setNewPromo(p => ({ ...p, discount: +e.target.value }))}
                className="w-full bg-white border border-[#c3c6d7]/40 rounded-lg px-3 py-2 text-sm text-[#0b1c30] focus:ring-1 focus:ring-[#004ac6] outline-none"
                data-testid="promo-discount-input" />
            </div>
            <button onClick={addPromo} data-testid="add-promo-btn"
              className="bg-[#004ac6] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#003ea8] transition-all flex items-center gap-1.5 shrink-0">
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>
        </div>

        {/* List */}
        {promotions.length === 0 ? (
          <div className="text-center py-8 text-[#c3c6d7] text-xs italic">No promotions created yet</div>
        ) : (
          <div className="space-y-2">
            {promotions.map(promo => (
              <div key={promo.id} data-testid={`promo-row-${promo.id}`}
                className={`flex items-center justify-between p-4 rounded-xl border transition-all ${promo.active ? 'bg-green-50/50 border-green-200' : 'bg-[#f8f9ff] border-[#c3c6d7]/20'}`}>
                <div className="flex items-center gap-3">
                  <button onClick={() => togglePromo(promo.id)} data-testid={`toggle-${promo.id}`} className="text-[#434655] hover:text-[#004ac6] transition-all">
                    {promo.active ? <ToggleRight className="w-6 h-6 text-green-500" /> : <ToggleLeft className="w-6 h-6" />}
                  </button>
                  <div>
                    <p className="text-sm font-semibold text-[#0b1c30] flex items-center gap-2">
                      {promo.name}
                      {promo.active && <span className="text-[9px] font-bold text-green-600 bg-green-100 px-1.5 py-0.5 rounded-full uppercase">Active</span>}
                    </p>
                    <p className="text-[10px] text-[#737686]">Created {new Date(promo.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-lg font-extrabold text-[#004ac6] flex items-center gap-1"><Percent className="w-4 h-4" /> {promo.discount}% OFF</span>
                  <button onClick={() => removePromo(promo.id)} data-testid={`delete-${promo.id}`}
                    className="p-2 rounded-lg text-[#c3c6d7] hover:bg-red-50 hover:text-red-600 transition-all"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Coupon Codes */}
        <div className="mt-8 pt-6 border-t border-[#c3c6d7]/30">
          <h3 className="text-sm font-bold uppercase tracking-widest text-[#434655] flex items-center gap-2 mb-4">
            <Ticket className="w-4 h-4 text-[#7c3aed]" /> Coupon Codes
          </h3>
          <p className="text-xs text-[#737686] mb-4">Create coupon codes that users can redeem on the pricing page for free reports.</p>

          <div className="bg-[#f8f9ff] rounded-xl p-4 mb-5">
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#434655] mb-3">Create New Coupon</p>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label className="text-[10px] text-[#434655] block mb-1">Code</label>
                <input type="text" value={newCoupon.code} onChange={e => setNewCoupon(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                  placeholder="e.g. WELCOME2026, PARTNER50"
                  className="w-full bg-white border border-[#c3c6d7]/40 rounded-lg px-3 py-2 text-sm text-[#0b1c30] font-mono tracking-wider focus:ring-1 focus:ring-[#7c3aed] outline-none"
                  data-testid="coupon-code-input" />
              </div>
              <div className="w-24">
                <label className="text-[10px] text-[#434655] block mb-1">Free Reports</label>
                <input type="number" min="1" max="50" value={newCoupon.reports} onChange={e => setNewCoupon(p => ({ ...p, reports: +e.target.value }))}
                  className="w-full bg-white border border-[#c3c6d7]/40 rounded-lg px-3 py-2 text-sm text-[#0b1c30] focus:ring-1 focus:ring-[#7c3aed] outline-none"
                  data-testid="coupon-reports-input" />
              </div>
              <div className="w-24">
                <label className="text-[10px] text-[#434655] block mb-1">Max Uses</label>
                <input type="number" min="1" max="10000" value={newCoupon.max_uses} onChange={e => setNewCoupon(p => ({ ...p, max_uses: +e.target.value }))}
                  className="w-full bg-white border border-[#c3c6d7]/40 rounded-lg px-3 py-2 text-sm text-[#0b1c30] focus:ring-1 focus:ring-[#7c3aed] outline-none"
                  data-testid="coupon-maxuses-input" />
              </div>
              <button onClick={addCoupon} data-testid="add-coupon-btn"
                className="bg-[#7c3aed] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#6d28d9] transition-all flex items-center gap-1.5 shrink-0">
                <Plus className="w-4 h-4" /> Create
              </button>
            </div>
          </div>

          {coupons.length === 0 ? (
            <div className="text-center py-6 text-[#c3c6d7] text-xs italic">No coupon codes created yet</div>
          ) : (
            <div className="space-y-2">
              {coupons.map(c => (
                <div key={c.id} data-testid={`coupon-row-${c.id}`}
                  className={`flex items-center justify-between p-4 rounded-xl border transition-all ${c.times_used >= c.max_uses ? 'bg-[#f8f9ff] border-[#c3c6d7]/20 opacity-60' : 'bg-[#f3f0ff]/50 border-[#7c3aed]/20'}`}>
                  <div className="flex items-center gap-3">
                    <Ticket className="w-5 h-5 text-[#7c3aed]" />
                    <div>
                      <p className="text-sm font-bold text-[#0b1c30] font-mono tracking-wider flex items-center gap-2">
                        {c.code}
                        <button onClick={() => { navigator.clipboard.writeText(c.code); toast.success('Copied!'); }}
                          className="p-0.5 rounded hover:bg-[#e5eeff] text-[#737686] hover:text-[#004ac6] transition-all">
                          <Copy className="w-3 h-3" />
                        </button>
                        {c.times_used >= c.max_uses && <span className="text-[9px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded-full normal-case">Fully Used</span>}
                      </p>
                      <p className="text-[10px] text-[#737686]">
                        {c.reports} report{c.reports > 1 ? 's' : ''} per use · {c.times_used}/{c.max_uses} redeemed · Created {new Date(c.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => deleteCoupon(c.id)} data-testid={`delete-coupon-${c.id}`}
                    className="p-2 rounded-lg text-[#c3c6d7] hover:bg-red-50 hover:text-red-600 transition-all"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* AI Model Rates */}
      <section>
        <h2 className="text-sm font-bold uppercase tracking-widest text-[#434655] mb-3 flex items-center gap-2">
          <Cpu className="w-4 h-4 text-[#004ac6]" /> Underlying AI Model Rates
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { d: COST_DATA.text, label: 'Text Generation', clr: '#004ac6', extra: <><div className="flex justify-between text-xs"><span className="text-[#434655]">Input</span><span className="font-semibold">${COST_DATA.text.inputRate.toFixed(2)} / {COST_DATA.text.unit}</span></div><div className="flex justify-between text-xs"><span className="text-[#434655]">Output</span><span className="font-semibold">${COST_DATA.text.outputRate.toFixed(2)} / {COST_DATA.text.unit}</span></div></> },
            { d: COST_DATA.image, label: 'Image Generation (Primary)', clr: '#00A651', extra: <div className="flex justify-between text-xs"><span className="text-[#434655]">Rate</span><span className="font-semibold">${COST_DATA.image.rate.toFixed(2)} / {COST_DATA.image.unit}</span></div> },
            { d: COST_DATA.image_claude, label: 'Claude HTML Fallback', clr: '#7c3aed', extra: <><div className="flex justify-between text-xs"><span className="text-[#434655]">Rate (est.)</span><span className="font-semibold">${COST_DATA.image_claude.rate.toFixed(2)} / {COST_DATA.image_claude.unit}</span></div><div className="flex justify-between text-[10px]"><span className="text-[#737686]">~{COST_DATA.image_claude.inputTokens}in / {COST_DATA.image_claude.outputTokens.toLocaleString()}out tokens</span></div></> },
            { d: COST_DATA.audio, label: 'Audio (TTS)', clr: '#f59e0b', extra: <div className="flex justify-between text-xs"><span className="text-[#434655]">Rate</span><span className="font-semibold">${COST_DATA.audio.rate.toFixed(2)} / {COST_DATA.audio.unit}</span></div> },
          ].map(({ d, label, clr, extra }) => (
            <div key={label} className="bg-white rounded-xl shadow-sm p-5" style={{ borderTop: `4px solid ${clr}` }}>
              <p className="text-[10px] text-[#434655] uppercase tracking-widest mb-1">{label}</p>
              <p className="text-sm font-bold text-[#0b1c30]">{d.model}</p>
              <p className="text-[10px] text-[#737686] mt-1">{d.provider}</p>
              <div className="mt-3 space-y-1">{extra}</div>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-[#737686] mt-2 italic">
          <strong>Fallback logic:</strong> When Gemini Nano Banana is unavailable (upstream outage), admin can flip the Illustration Engine toggle in <code>/platform/admin → User Management</code> to Claude HTML. Claude generates A4-safe HTML infographics which are rendered to PNG via headless Chromium. ~4× the per-image cost but 100% independent of the Gemini proxy.
        </p>
      </section>

      {/* Cost Breakdown Table */}
      <section>
        <h2 className="text-sm font-bold uppercase tracking-widest text-[#434655] mb-3 flex items-center gap-2">
          <Calculator className="w-4 h-4 text-[#004ac6]" /> Cost Per Report
        </h2>
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="cost-breakdown-table">
            <thead>
              <tr className="bg-[#f8f9ff] text-[10px] uppercase tracking-widest text-[#434655]">
                <th className="text-left px-5 py-3 font-semibold">Component</th>
                <th className="text-left px-5 py-3 font-semibold">Model</th>
                <th className="text-center px-5 py-3 font-semibold">Calls</th>
                <th className="text-right px-5 py-3 font-semibold">Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#c3c6d7]/20">
              {REPORT_COMPONENTS.map(c => {
                const Icon = c.icon; const cost = calcCost(c);
                return (
                  <tr key={c.id} className="hover:bg-[#f8f9ff]">
                    <td className="px-5 py-3 font-medium text-[#0b1c30] flex items-center gap-2"><Icon className="w-4 h-4 text-[#004ac6]" /> {c.label}</td>
                    <td className="px-5 py-3 text-[#434655] text-xs">{c.type === 'text' ? COST_DATA.text.model : c.type === 'image' ? COST_DATA.image.model : c.type === 'audio' ? COST_DATA.audio.model : 'Local'}</td>
                    <td className="px-5 py-3 text-center text-[#434655]">{c.calls}</td>
                    <td className="px-5 py-3 text-right font-bold">{cost === 0 ? <span className="text-green-600">FREE</span> : `$${cost.toFixed(2)}`}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-[#0b1c30] text-white">
                <td colSpan={3} className="px-5 py-3 text-xs font-bold uppercase tracking-widest">Total Cost Per Report</td>
                <td className="px-5 py-3 text-right font-extrabold text-lg">${REPORT_COMPONENTS.reduce((s, c) => s + calcCost(c), 0).toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      {/* Revenue Projection */}
      <section>
        <h2 className="text-sm font-bold uppercase tracking-widest text-[#434655] mb-3 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#004ac6]" /> Revenue Projection (Monthly)
        </h2>
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="revenue-projection-table">
            <thead>
              <tr className="bg-[#f8f9ff] text-[10px] uppercase tracking-widest text-[#434655]">
                <th className="text-left px-5 py-3 font-semibold">Reports</th>
                {tierCosts.map(t => <th key={t.name} className="text-center px-5 py-3 font-semibold" colSpan={2}>{t.name} ({markup.toLocaleString()}x)</th>)}
              </tr>
              <tr className="bg-[#f8f9ff]/50 text-[10px] text-[#737686]">
                <th className="px-5 py-1"></th>
                {tierCosts.map(t => <React.Fragment key={t.name}><th className="px-3 py-1 text-center">Revenue</th><th className="px-3 py-1 text-center">Profit</th></React.Fragment>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#c3c6d7]/20">
              {[10, 25, 50, 100, 250, 500].map(n => (
                <tr key={n} className="hover:bg-[#f8f9ff]">
                  <td className="px-5 py-3 font-semibold text-[#0b1c30]">{n}</td>
                  {tierCosts.map(t => {
                    let sell = t.cost * markup;
                    if (activePromo) sell *= (1 - activePromo.discount / 100);
                    const rev = n * sell; const profit = rev - (n * t.cost);
                    return (
                      <React.Fragment key={t.name}>
                        <td className="px-3 py-3 text-center text-[#0b1c30] font-medium">${rev.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                        <td className="px-3 py-3 text-center text-green-600 font-bold">${profit.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Notes */}
      <section className="bg-[#f8f9ff] rounded-xl p-6 border border-[#c3c6d7]/30">
        <h2 className="text-sm font-bold text-[#0b1c30] flex items-center gap-2 mb-3"><Info className="w-4 h-4 text-[#004ac6]" /> Notes</h2>
        <ul className="space-y-1.5 text-xs text-[#434655]">
          <li>Each package purchase grants the user <strong>1 country report</strong>. Users must purchase again for additional reports.</li>
          <li>Users can regenerate each of the 31 sections up to <strong>10 times within 3 months</strong> of report creation.</li>
          <li>Changes are previewed instantly on this page. Click <strong>"Publish to Customers"</strong> to push pricing live.</li>
          <li>Only the first active promotion applies to public pricing.</li>
          <li><strong>Cinematic Brief</strong> (Sora 2 video) is temporarily disabled — planned for the next release.</li>
          <li><strong>Illustration fallback:</strong> If Gemini is toggled off (admin → User Management), each illustration uses Claude HTML → PNG at <strong>~${COST_DATA.image_claude.rate.toFixed(2)}/image</strong> instead of ${COST_DATA.image.rate.toFixed(2)}/image — a ~${Math.round((COST_DATA.image_claude.rate / COST_DATA.image.rate))}× cost increase. A full Strategist/Sovereign report (30 illustrations) moves from ~${(30 * COST_DATA.image.rate).toFixed(2)} to ~${(30 * COST_DATA.image_claude.rate).toFixed(2)}.</li>
        </ul>
      </section>
      </div>
    </div>
  );
}
